import React, { useEffect, useState, useCallback } from "react";
import Cookies from "js-cookie";
import { useSelector } from "react-redux";

import "./CookieConsentManager.css";
import ManageCookiesModal from "./ManageCookiesModal";
import CookieConsentContext from "./CookieConsentContext";
import useVisitorId from "@/hooks/useVisitorId";
import { updateGTMConsent } from "@/utils/analytics";

const MARKETING_COOKIES = [
  "AWSALBCORS",
  "AWSALB",
  "1P_JAR",
  "cto_bundle",
  "cto_bidid",
  "loggedInLastName",
  "numberOfPdpViews",
  "userVisitedOneTime",
  "numberOfCustomerViews",
  "lastActivityDateTime",
  "loggedInPhone",
  "lastVisitedUserId",
  "loggedInFirstName",
  "userId",
  "subscribeEmail",
  "loggedInEmailActive",
  "visitorId",
];

const PERFORMANCE_COOKIES = [
  "pbjs-unifiedid",
  "_lr_retry_request",
  "_pbjs_userid_consent_data",
  "_fcrt",
  "_fcat",
  "_lr_env_src_ats",
  "lastVisited",
  "loggedInEmail",
];

const DEFAULT_COOKIE_TYPES = {
  _necessary: true,
  _performance: false,
  _marketing: false,
};

const ACCEPTED_COOKIE_TYPES = {
  _necessary: true,
  _performance: true,
  _marketing: true,
};

// Cookie expiry durations (in days)
const ACCEPT_EXPIRY_DAYS = 365; // 12 months
const REJECT_EXPIRY_DAYS = 182; // 6 months
const DISMISS_EXPIRY_DAYS = 30; // soft dismiss — banner reappears after 30 days

const CookieConsentManager = ({ children }) => {
  const { visitorId, loading } = useVisitorId();
  const user = useSelector((state) => state.user);
  const userId = user?.user?.realbidUserId || "";

  const [isInitialized, setIsInitialized] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [cookieConsent, setCookieConsent] = useState(null);
  const [cookieTypes, setCookieTypes] = useState(DEFAULT_COOKIE_TYPES);

  const getCookiePreferences = () => {
    try {
      const raw = Cookies.get("cookiePreferences");
      return raw ? JSON.parse(raw) : null;
    } catch (error) {
      console.error("Error parsing cookie preferences:", error);
      return null;
    }
  };

  const savePreferencesCookie = () => {
    setIsModalVisible(false);

    if (!cookieTypes._marketing) clearCookies(MARKETING_COOKIES);
    if (!cookieTypes._performance) clearCookies(PERFORMANCE_COOKIES);

    const allDisabled = Object.values(cookieTypes).every((value) => !value);
    if (allDisabled) {
      denyOptionalCookies();
      return;
    }

    Cookies.set("cookiePreferences", JSON.stringify(cookieTypes), {
      expires: ACCEPT_EXPIRY_DAYS,
    });
    Cookies.set("cookieConsent", "accepted", { expires: ACCEPT_EXPIRY_DAYS });
    setCookieConsent("accepted");

    updateGTMConsent({
      event_name: "cookie_consent_accepted_user_selected",
      ad_storage: cookieTypes._marketing ? "granted" : "denied",
      analytics_storage: cookieTypes._performance ? "granted" : "denied",
      functionality_storage: "granted",
      personalization_storage: cookieTypes._marketing ? "granted" : "denied",
      ad_personalization: cookieTypes._marketing ? "granted" : "denied",
      ad_user_data: cookieTypes._marketing ? "granted" : "denied",
    }, { visitorId });
  };

  const updateCookieType = (event, type) => {
    const isChecked = event.target.checked;
    setCookieTypes((prevTypes) => ({ ...prevTypes, [type]: isChecked }));
  };

  const clearCookies = (cookies) => {
    cookies.forEach((cookieName) => Cookies.remove(cookieName));
  };

  const clearAllNonEssentialCookies = () => {
    clearCookies(MARKETING_COOKIES);
    clearCookies(PERFORMANCE_COOKIES);
  };

  const closeConsentPopup = () => {
    setIsModalVisible(false);
    setCookieConsent("dismissed");
    setCookieTypes(DEFAULT_COOKIE_TYPES);

    // Persist dismissal for 30 days so the banner doesn't reappear every page load
    Cookies.set("cookieConsent", "dismissed", { expires: DISMISS_EXPIRY_DAYS });
    Cookies.set("cookiePreferences", JSON.stringify(DEFAULT_COOKIE_TYPES), {
      expires: DISMISS_EXPIRY_DAYS,
    });

    clearAllNonEssentialCookies();

    updateGTMConsent({
      event_name: "main_cookie_consent_closed",
      ad_storage: "denied",
      analytics_storage: "denied",
      functionality_storage: "granted",
      personalization_storage: "denied",
      ad_personalization: "denied",
      ad_user_data: "denied",
    }, { visitorId });
  };

  const denyOptionalCookies = () => {
    setIsModalVisible(false);
    setCookieConsent("accepted");
    setCookieTypes(DEFAULT_COOKIE_TYPES);

    Cookies.set("cookieConsent", "accepted", { expires: REJECT_EXPIRY_DAYS });
    Cookies.set("cookiePreferences", JSON.stringify(DEFAULT_COOKIE_TYPES), {
      expires: REJECT_EXPIRY_DAYS,
    });

    clearAllNonEssentialCookies();

    updateGTMConsent({
      event_name: "cookie_consent_accepted_only_essentials",
      ad_storage: "denied",
      analytics_storage: "denied",
      functionality_storage: "granted",
      personalization_storage: "denied",
      ad_personalization: "denied",
      ad_user_data: "denied",
    }, { visitorId });
  };

  const acceptCookies = () => {
    setCookieConsent("accepted");
    setCookieTypes(ACCEPTED_COOKIE_TYPES);

    Cookies.set("cookieConsent", "accepted", { expires: ACCEPT_EXPIRY_DAYS });
    Cookies.set("cookiePreferences", JSON.stringify(ACCEPTED_COOKIE_TYPES), {
      expires: ACCEPT_EXPIRY_DAYS,
    });

    updateGTMConsent({
      event_name: "cookie_consent_accepted",
      ad_storage: "granted",
      analytics_storage: "granted",
      functionality_storage: "granted",
      personalization_storage: "granted",
      ad_personalization: "granted",
      ad_user_data: "granted",
    }, { visitorId });
  };

  useEffect(() => {
    const consent = Cookies.get("cookieConsent");
    const preferences = getCookiePreferences();

    try {
      if (consent === "denied") {
        denyOptionalCookies();
      } else if (consent === "dismissed") {
        // User previously dismissed — show banner again after cookie expires (30 days)
        setCookieConsent("dismissed");
        setCookieTypes(DEFAULT_COOKIE_TYPES);
      } else if (consent && preferences) {
        const allDisabled =
          !preferences._performance && !preferences._marketing;
        setCookieConsent(allDisabled ? "denied" : "accepted");
        setCookieTypes(preferences);
      } else {
        setCookieConsent(null);
      }
    } catch (error) {
      console.error("Error parsing cookie preferences:", error);
    } finally {
      setIsInitialized(true);
    }
  }, []);

  const openManageCookies = useCallback(() => {
    // Load saved preferences for logged-in users, defaults for others
    const preferences = getCookiePreferences();
    const activePrefs = preferences || DEFAULT_COOKIE_TYPES;
    setCookieTypes(activePrefs);
    setIsModalVisible(true);
    updateGTMConsent({
      event_name: "manage_cookies_footer_click",
      ad_storage: activePrefs._marketing ? "granted" : "denied",
      security_storage: "granted",
      analytics_storage: activePrefs._performance ? "granted" : "denied",
      functionality_storage: "granted",
      personalization_storage: activePrefs._marketing ? "granted" : "denied",
      ad_personalization: activePrefs._marketing ? "granted" : "denied",
      ad_user_data: activePrefs._marketing ? "granted" : "denied",
    }, { visitorId });
  }, [visitorId]);

  if (!isInitialized) {
    return (
      <CookieConsentContext.Provider value={{ openManageCookies }}>
        {children}
      </CookieConsentContext.Provider>
    );
  }

  return (
    <CookieConsentContext.Provider value={{ openManageCookies }}>
      {children}
      {/* Cookie Consent Overlay */}
      {!cookieConsent && (
        <div className="cookie-consent-banner">
          <div className="cookie-content">
            <h4 className="cookie-title">We value your privacy</h4>

            {/* cookies text */}
            <p className="cookie-text">
              We use cookies to track website usage and improve the user
              experience. You can accept all cookies or customize your
              preferences at any time. For more information, please see our{" "}
              <a
                href="https://www.altisource.com/Privacy-Policy/"
                className="privacy-link"
                target="_blank"
                rel="noopener noreferrer"
              >
                Privacy Policy
              </a>
              .
            </p>
            {/* cookies button */}
            <div className="cookie-buttons">
              <button
                className="customize-btn"
                onClick={() => {
                  setIsModalVisible(true);
                  updateGTMConsent({
                    event_name: "manage_cookies",
                    ad_storage: "denied",
                    security_storage: "granted",
                    analytics_storage: "denied",
                    functionality_storage: "denied",
                    personalization_storage: "denied",
                    ad_personalization: "denied",
                    ad_user_data: "denied",
                  }, { visitorId });
                }}
              >
                Customize
              </button>

              <button className="reject-btn" onClick={denyOptionalCookies}>
                Reject All
              </button>
              <button className="accept-btn" onClick={acceptCookies}>
                Accept All
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Manage Cookies Modal */}
      <ManageCookiesModal
        show={isModalVisible}
        userId={userId}
        visitorId={visitorId}
        onRejectOptional={denyOptionalCookies}
        marketingCookies={cookieTypes._marketing}
        performanceCookies={cookieTypes._performance}
        handleCookieTypeChange={updateCookieType}
        onSavePreferences={savePreferencesCookie}
        handleClose={() => {
          // Revert to saved preferences since user didn't confirm changes
          const savedPrefs = getCookiePreferences() || DEFAULT_COOKIE_TYPES;
          setCookieTypes(savedPrefs);
          setIsModalVisible(false);

          updateGTMConsent({
            event_name: "manage_cookie_consent_closed",
            ad_storage: savedPrefs._marketing ? "granted" : "denied",
            security_storage: "granted",
            analytics_storage: savedPrefs._performance ? "granted" : "denied",
            functionality_storage: "granted",
            personalization_storage: savedPrefs._marketing ? "granted" : "denied",
            ad_personalization: savedPrefs._marketing ? "granted" : "denied",
            ad_user_data: savedPrefs._marketing ? "granted" : "denied",
          }, { visitorId });
        }}
      />
    </CookieConsentContext.Provider>
  );
};

export default CookieConsentManager;
